<script lang="ts" setup>
import { computed, VNode } from 'vue';

import { InputVariant } from '@/types';

interface Props {
  label?: string;
  labelIcon?: string | object | (() => VNode) | null;
  modelValue?: string | number;
  required?: boolean;
  placeholder?: string;
  rows?: number
  disabled?: boolean;
  testId?: string;
  variant?: InputVariant;
  textareaClass?: string;
  textareaStyle?: Record<string, string>;
};

const props = withDefaults(defineProps<Props>(), {
  label: "",
  placeholder: "",
  labelIcon: null,
  modelValue: "",
  required: false,
  disabled: false,
  rows: 1,
  variant: "primary",
  testId: "textarea",
});

const variantClass = computed(() => props.variant ?? 'primary');
const backgroundStyle = computed(() => ({
  background:
    props.modelValue && ["primary", "admin"].includes(props.variant)
      ? "rgba(28, 86, 145, 0.05)"
      : "none",
}));
</script>

<template>
  <label :class="['w-100', variantClass]">
    <div v-if="label" class="textarea-label d-flex align-items-center">
      {{ label }}
      <span v-if="required">&ast;</span>
      <component v-if="labelIcon" :is="labelIcon" class="ms-1" />
    </div>
    <textarea
      @input="e => $emit('update:modelValue', (e.target as HTMLTextAreaElement).value)"
      :value="modelValue"
      :rows="rows"
      :data-testid="testId"
      :disabled="disabled"
      :placeholder="placeholder"
      :style="{ ...backgroundStyle, ...textareaStyle }"
      :class="textareaClass"
      v-bind="$attrs"
    />
  </label>
</template>

<style scoped>
label .textarea-label {
  margin-bottom: 0.25rem;
  font-size: var(--font-xs);
  font-weight: 400;
  text-transform: capitalize;
}

label textarea {
  padding: 0.25rem 0.5rem;
  width: 100%;
  font-size: var(--font-sm);
  font-weight: 400;
}

label textarea::placeholder {
  color: rgba(28, 86, 145, 0.2);
  font-size: var(--font-xs);
}

label textarea:focus {
  box-shadow: 0 0 0 2px color-mix(in srgb, currentColor 10%, transparent);
  outline: none;
}

label textarea:disabled {
  color: rgba(0, 0, 0, 0.5);
  opacity: 0.75;
}

@media screen and (min-width: 1024px) {
  label .textarea-label {
    font-size: var(--font-sm);
  }
}

/* Variant Styles */

/* Primary Variant */
label.primary .textarea-label {
  color: var(--ch-black);
}

label.primary textarea {
  color: var(--ch-black);
  border: 1px solid var(--ch-blue-100);
  border-radius: 6px;
}

label.primary textarea:focus {
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--ch-blue-100) 15%, transparent);
}

/* AdminV1 Variant */
label.admin .textarea-label {
  color: var(--ch-text-head);
}

label.admin textarea {
  color: var(--ch-text-head);
  border: 1px solid var(--ch-input);
  border-radius: 5px;
}

/* Grey Variant */
label.grey .textarea-label {
  color: var(--ch-black);
}

label.grey textarea {
  color: var(--ch-black);
  border: 1px solid var(--ch-border-grey-30);
  border-radius: 6px;
}

label.grey textarea::placeholder {
  color: var(--ch-border-grey);
}

label.grey textarea:focus {
  border: 2px solid var(--ch-blue-100);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--ch-blue-100) 10%, transparent);
}

/* ReadOnly Variant */
label textarea[readonly]::placeholder {
  color: var(--ch-black) !important; 
  font-size: var(--font-sm) !important;
}

label textarea[readonly] { 
  border: 1px solid var(--ch-border-grey-30) !important; 
  border-radius: 6px !important;
  color: var(--ch-black) !important;
  font-size: var(--font-sm) !important;
  background-color: var(--ch-readonly) !important;
}

label textarea[readonly]:focus {
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--ch-readonly) 15%, transparent) !important;
  outline: none !important;
  border: 1px solid var(--ch-border-grey-30) !important;
}
</style>
